/**
 * Reading an error response body without letting the read itself throw.
 *
 * Not every failure comes back as JSON. Django's request-logging middleware
 * returns `text/plain` "Internal Server Error" for anything a view did not
 * catch, and nginx answers 502/503/504 with its own HTML page — so the
 * reflexive `await response.json()` throws a SyntaxError on exactly the
 * responses we most need to describe, and the real status is lost behind a
 * parse failure.
 *
 * The content-type check alone is not enough either: nginx has been seen to
 * label an HTML error page `application/json`. We require both a JSON
 * content-type and a body that actually starts like JSON before parsing.
 */

/** True when `text` looks like a JSON object or array. */
function looksLikeJson(text) {
  if (!text) return false;
  const first = text.trim()[0];
  return first === '{' || first === '[';
}

/**
 * Read `response`'s body once and return both the raw text and, when it really
 * is JSON, the parsed object.
 *
 * Reads a clone, so the caller is free to read the original response too.
 * Never throws and never rejects: an unreadable body is `{ body: null, text: null }`,
 * which callers treat the same as a non-JSON body.
 *
 * @param {Response} response
 * @returns {Promise<{ body: object|null, text: string|null }>}
 */
export async function readErrorBody(response) {
  if (!response || typeof response.clone !== 'function') {
    return { body: null, text: null };
  }

  let text = null;
  try {
    text = await response.clone().text();
  } catch {
    // Body already consumed, or the connection died mid-read. Either way there
    // is nothing to describe the failure with; the status has to carry it.
    return { body: null, text: null };
  }

  const contentType = (response.headers && response.headers.get('content-type')) || '';
  const claimsJson = contentType.toLowerCase().includes('application/json');

  if (!claimsJson || !looksLikeJson(text)) {
    return { body: null, text };
  }

  try {
    const parsed = JSON.parse(text);
    // A bare string or number is valid JSON but carries none of the error
    // shapes we look for, so treat it as unparsed rather than pretending.
    if (parsed === null || typeof parsed !== 'object') {
      return { body: null, text };
    }
    return { body: parsed, text };
  } catch {
    return { body: null, text };
  }
}

export default readErrorBody;
